/* eslint-disable */
/* Vendored file — lint is off on purpose. It is re-copied from the canonical source, never
 * hand-edited, so any Prettier/ESLint autofix here would turn the next sync into a merge
 * conflict. `.eslintignore` / `.prettierignore` cover the CLI; this line covers editors whose
 * ESLint server does not pick those up. See the sync note below. */

/* ==========================================================================
 * svgLabelBadge — shared SVG geometry + text layout for text labels/badges.
 *
 * COPY - DO NOT EDIT IN ISOLATION. The canonical file is
 *   product-label-extensions/frontend/src/services/svgLabelBadge.js
 * Any change there MUST be applied here in the same change (and vice versa).
 * See .claude/features/svg_label_and_badge.md §3.
 *
 * This is the THIRD copy (support tool). The other two live at
 *   product-label-extensions/frontend/src/services/svgLabelBadge.js  (canonical)
 *   product-labels-2.0-frontend/src/utility/svgLabelBadge.js         (admin app)
 * A support agent editing a size here must see the same drawing the merchant sees, so this
 * file must be re-copied verbatim whenever the canonical one changes. Do not hand-edit it.
 *
 * Pure module: no dependency on window.SmartifyAppDECO, no condition
 * evaluation, no AutoText token resolution. Callers build a `__decoLayout`
 * config object on the <svg> node and call layoutSvgNode/scheduleSvgLayout.
 *
 * Shapes are drawn as <polygon>/<ellipse>/<path> (ported from the clip-path
 * rules in label-badge.css) and text as <text>/<tspan>. Text is wrapped and
 * fitted with off-DOM Canvas measurement — no clientHeight binary-search and
 * therefore no forced reflow.
 * ========================================================================== */

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const REM_PX = 16;

// Height of the logical viewBox, in user units. Fixing it at 100 makes `font_size` read directly
// as "percent of the label's height", and makes every inner dimension resolution-independent.
const VIEWBOX_HEIGHT = 100;

// Shapes whose CSS class forced a square box. Their geometry only looks right at 1:1, so the
// viewBox has to be square too — the viewBox aspect and the element's CSS aspect-ratio must always
// agree or `preserveAspectRatio="none"` would stretch the text.
const ONE_ONE_SHAPES = [
  'pl-text-circle',
  'pl-text-star',
  'pl-text-trapezoid',
  'pl-text-trapezoid-bottom',
  'pl-text-triangle',
  'pl-text-triangle-bottom',
];

/**
 * The single source of truth for a label's aspect ratio.
 *
 * Callers MUST use the returned `viewW`/`viewH` for the viewBox *and* `cssAspect` for the
 * element's CSS aspect-ratio. Deriving them separately is how the text ends up stretched.
 *
 * `imageAspect` (naturalWidth/naturalHeight) wins when an image label is set to keep its ratio.
 */
const effectiveAspect = ({ widthTmd, heightTmd, shapeClass, hasImage, ratioSize, imageAspect }) => {
  let ratio;
  if (hasImage && ratioSize && imageAspect > 0) {
    ratio = imageAspect;
  } else if (!hasImage && ONE_ONE_SHAPES.includes(shapeClass)) {
    ratio = 1;
  } else {
    const w = parseFloat(widthTmd);
    const h = parseFloat(heightTmd);
    ratio = w > 0 && h > 0 ? w / h : 1;
  }
  return {
    viewW: VIEWBOX_HEIGHT * ratio,
    viewH: VIEWBOX_HEIGHT,
    cssAspect: `${VIEWBOX_HEIGHT * ratio} / ${VIEWBOX_HEIGHT}`,
  };
};

// Monotonic per-instance id so gradient/clip <defs> ids are unique across the whole document
// (a label repeated on many cards, or cloned into a group, must not share ids).
let decoUid = 0;
// Callers that build their own __decoLayout must allocate the uid through this, so every
// node in the document (originals and clones alike) gets a distinct gradient/clip id.
const nextDecoUid = () => ++decoUid;

const svgCreate = (tag, attrs) => {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const k in attrs) {
      if (attrs[k] === null || attrs[k] === undefined) continue;
      if (k === 'xlink:href') el.setAttributeNS(XLINK_NS, k, attrs[k]);
      else el.setAttribute(k, attrs[k]);
    }
  }
  return el;
};

/* --------------------------------------------------------------------------------------------
 * Scene nodes
 *
 * Every builder below returns PLAIN DATA describing the drawing, never DOM:
 *
 *   { tag, attrs, style?, text?, children? }      // text = textContent, used by <tspan>
 *
 * `buildSvgScene(cfg)` composes the whole tree; two thin emitters turn it into something real:
 * `renderSceneInto()` here (storefront, imperative DOM) and a React component in the admin
 * (`components/svg-label-badge`, see §5.5) that maps the same tree to JSX. Geometry and text
 * measurement therefore exist exactly once, while React gets to diff instead of us rebuilding.
 *
 * Two conventions the emitters share, so both produce identical output:
 *  - `null`/`undefined` attribute values are SKIPPED (they are not `""`).
 *  - key insertion order is the attribute order.
 * -------------------------------------------------------------------------------------------- */
const sceneNode = (tag, attrs, extra) => ({ tag, attrs: attrs || {}, ...extra });

// Shallow copy for reusing a shape as a clip path — shape nodes never have children, which is
// exactly what the old `shapeEl.cloneNode(false)` relied on.
const sceneClone = node => sceneNode(node.tag, { ...node.attrs });

// Shape geometry ported from the clip-path polygons in label-badge.css.
// Vertices are fractions of the box (0..1) => scaled by (W, H) at draw time.
// NOTE: 'pl-text-rectangle' is deliberately NOT listed here. Its clip-path is the full box
// (a no-op), so it must fall through to the border-radius / plain-<rect> branches of
// buildShape — otherwise a rectangle label with border_radius would render square-cornered.
// Legacy kept the radius for this shape (the full-box clip-path doesn't cut the rounded
// background); every non-rectangular shape loses it, which buildShape also reproduces.
const SHAPE_POLYGONS = {
  'pl-text-left-point': [[0.25, 0], [1, 0], [1, 1], [0.25, 1], [0, 0.5]],
  'pl-text-shape-5': [[1, 0], [1, 1], [0, 1], [0.25, 0.5], [0, 0]],
  'pl-text-star': [
    [0.5, 0], [0.6323, 0.093], [0.7939, 0.0955], [0.8462, 0.2484], [0.9755, 0.3455],
    [0.928, 0.5], [0.9755, 0.6545], [0.8462, 0.7516], [0.7939, 0.9045], [0.6323, 0.907],
    [0.5, 1], [0.3677, 0.907], [0.2061, 0.9045], [0.1538, 0.7516], [0.0245, 0.6545],
    [0.072, 0.5], [0.0245, 0.3455], [0.1538, 0.2484], [0.2061, 0.0955], [0.3677, 0.093],
  ],
  'pl-text-trapezoid': [[0.4, 0], [1, 0.6], [1, 1], [0, 0]],
  'pl-text-trapezoid-bottom': [[0, 0.4], [0, 0], [1, 1], [0.6, 1]],
  'pl-text-triangle': [[1, 0], [0, 0], [1, 1]],
  'pl-text-triangle-bottom': [[0, 0], [0, 1], [1, 1]],
  'pl-arrow-left': [[0.85, 0], [1, 0.5], [0.85, 1], [0, 1], [0.15, 0.5], [0, 0]],
  'pl-arrow-right': [[1, 0], [0.85, 0.5], [1, 1], [0.15, 1], [0, 0.5], [0.15, 0]],
  'pl-chevron-rectangle': [[1, 0], [0.85, 0.5], [1, 1], [0, 1], [0.15, 0.5], [0, 0]],
  'pl-chevron-left': [[1, 0], [0.85, 0.5], [1, 1], [0, 1], [0, 0.5], [0, 0]],
  'pl-chevron-right': [[1, 0], [1, 0.5], [1, 1], [0, 1], [0.15, 0.5], [0, 0]],
  'pl-slanted-chevron-left': [[1, 0], [0.9, 0.5], [1, 1], [0.15, 1], [0, 0]],
  'pl-slanted-chevron-right': [[1, 0], [0.85, 1], [0, 1], [0.1, 0.5], [0, 0]],
  'pl-slanted-left': [[0, 0], [1, 0], [1, 1], [0.15, 1]],
  'pl-slanted-right': [[0, 0], [1, 0], [0.85, 1], [0, 1]],
  'pl-diamond': [[0.15, 0], [0.85, 0], [1, 0.5], [0.85, 1], [0.15, 1], [0, 0.5]],
  'pl-flat-arrow-right': [[0, 0], [0.75, 0], [1, 0.5], [0.75, 1], [0, 1]],
  'pl-tilt-side': [[0.1, 0], [1, 0], [0.9, 1], [0, 1]],
};

// Rounded-rect shapes: [tl, tr, br, bl] corner radii in px (from the xywh(... round ...) rules).
const SHAPE_ROUNDED = {
  'pl-text-rectangle-border-corner': [10, 0, 10, 0],
  'pl-pill-end-shape': [0, 2 * REM_PX, 0, 2 * REM_PX],
  'pl-left-end-rounded': [4 * REM_PX, 0, 0, 0],
  'pl-right-end-rounded': [0, 4 * REM_PX, 0, 0],
  'pl-pill-left': [20 * REM_PX, 0, 0, 20 * REM_PX],
  'pl-pill-right': [0, 20 * REM_PX, 20 * REM_PX, 0],
  'pl-pill-shape': [20 * REM_PX, 20 * REM_PX, 20 * REM_PX, 20 * REM_PX],
};

// Per-shape text placement, ported from the `.pl-text` rules in label-badge.css.
// ax/ay = text anchor centre (fraction of box); padL/padR/padInline = horizontal
// inset (fraction); widthFactor shrinks the wrap width; rotate = 'auto' (atan(H/W))
// or a fixed degree.
const SHAPE_TEXT_LAYOUT = {
  'pl-text-left-point': { padL: 0.15 },
  'pl-text-shape-5': { padL: 0.2 },
  'pl-text-star': { widthFactor: 0.7 },
  'pl-text-trapezoid': { ax: 0.6, ay: 0.4, rotate: 'auto' },
  'pl-text-trapezoid-bottom': { ax: 0.4, ay: 0.6, rotate: 45 },
  'pl-text-triangle': { ax: 0.66, ay: 0.34, padInline: 0.15, rotate: 'auto' },
  'pl-text-triangle-bottom': { ax: 0.34, ay: 0.66, padInline: 0.15, rotate: 45 },
  'pl-arrow-left': { padL: 0.15, padR: 0.1 },
  'pl-arrow-right': { padL: 0.1, padR: 0.15 },
  'pl-chevron-rectangle': { padInline: 0.15 },
  'pl-chevron-left': { padR: 0.1 },
  'pl-chevron-right': { padL: 0.1 },
  'pl-left-end-rounded': { padL: 0.1 },
  'pl-right-end-rounded': { padR: 0.1 },
  'pl-slanted-chevron-left': { padInline: 0.1 },
  'pl-slanted-chevron-right': { padInline: 0.1 },
  'pl-slanted-left': { padL: 0.1 },
  'pl-slanted-right': { padR: 0.1 },
  'pl-diamond': { padInline: 0.1 },
  'pl-flat-arrow-right': { padR: 0.15 },
  'pl-tilt-side': { padInline: 0.05 },
};

const roundedRectPath = (W, H, radii) => {
  const m = Math.min(W, H) / 2;
  const [tl, tr, br, bl] = radii.map(v => Math.min(v, m));
  return [
    `M ${tl} 0`,
    `H ${W - tr}`,
    tr ? `A ${tr} ${tr} 0 0 1 ${W} ${tr}` : `L ${W} 0`,
    `V ${H - br}`,
    br ? `A ${br} ${br} 0 0 1 ${W - br} ${H}` : `L ${W} ${H}`,
    `H ${bl}`,
    bl ? `A ${bl} ${bl} 0 0 1 0 ${H - bl}` : `L 0 ${H}`,
    `V ${tl}`,
    tl ? `A ${tl} ${tl} 0 0 1 ${tl} 0` : `L 0 0`,
    'Z',
  ].join(' ');
};

// Build the shape node for a shape_class. borderRadiusPx rounds the plain rectangle.
const buildShape = (shapeClass, W, H, borderRadiusPx) => {
  if (shapeClass === 'pl-text-circle') {
    return sceneNode('ellipse', { cx: W / 2, cy: H / 2, rx: W / 2, ry: H / 2 });
  }
  if (SHAPE_ROUNDED[shapeClass]) {
    return sceneNode('path', { d: roundedRectPath(W, H, SHAPE_ROUNDED[shapeClass]) });
  }
  const poly = SHAPE_POLYGONS[shapeClass];
  if (poly) {
    return sceneNode('polygon', {
      points: poly.map(p => `${p[0] * W},${p[1] * H}`).join(' '),
    });
  }
  if (borderRadiusPx > 0) {
    return sceneNode('path', {
      d: roundedRectPath(W, H, [borderRadiusPx, borderRadiusPx, borderRadiusPx, borderRadiusPx]),
    });
  }
  return sceneNode('rect', { x: 0, y: 0, width: W, height: H });
};

// CSS linear-gradient angle (0deg = to top, clockwise) -> objectBoundingBox coords.
const buildGradient = (id, angle, c1, c2) => {
  const a = (parseFloat(angle) || 0) * (Math.PI / 180);
  const dx = Math.sin(a);
  const dy = -Math.cos(a);
  return sceneNode(
    'linearGradient',
    {
      id,
      x1: 0.5 - dx / 2,
      y1: 0.5 - dy / 2,
      x2: 0.5 + dx / 2,
      y2: 0.5 + dy / 2,
    },
    {
      children: [
        sceneNode('stop', { offset: '0%', 'stop-color': c1 }),
        sceneNode('stop', { offset: '100%', 'stop-color': c2 }),
      ],
    }
  );
};

// Off-DOM text measurement (no layout / reflow).
let _measureCanvas = null;
const measureCtx = () => {
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
  return _measureCanvas.getContext('2d');
};
const fontStringOf = (design, fontSize) => {
  const style = design.font_style?.style && design.font_style.style !== 'normal'
    ? design.font_style.style + ' '
    : '';
  const weight = design.font_style?.weight ? design.font_style.weight + ' ' : '';
  const family = design.font_family && design.font_family !== 'default'
    ? `'${design.font_family}'`
    : 'sans-serif';
  return `${style}${weight}${fontSize}px ${family}`;
};
const measureLine = (ctx, str, letterSpacing) => {
  const w = ctx.measureText(str).width;
  return w + (letterSpacing ? letterSpacing * Math.max(0, str.length - 1) : 0);
};
const lineHeightOf = (ctx, design, fontSize) => {
  ctx.font = fontStringOf(design, fontSize);
  const m = ctx.measureText('Mg');
  const lh = (m.fontBoundingBoxAscent || 0) + (m.fontBoundingBoxDescent || 0);
  return lh || fontSize * 1.2;
};

/**
 * Line height as it will actually be PAINTED: font metrics, overridden by a CSS `line-height`
 * (unitless multiplier or `px`; `normal` keeps the metrics).
 *
 * Every place that measures a block's height must use this, not `lineHeightOf`. The overflow guard
 * and `fitText` used the metric-only value while the renderer honoured the CSS one, so
 * `line-height: 2.5` produced a block over twice as tall as the content box that the guard happily
 * passed — and the shape's clipPath then sliced the first line's ascenders and the last line's
 * descenders off. The rich path already threaded this through `wrapRichText`/`fitRichText`.
 */
const effectiveLineHeight = (ctx, design, fontSize, cssLh) => {
  const metric = lineHeightOf(ctx, design, fontSize);
  if (cssLh && cssLh !== 'normal') {
    const v = parseFloat(cssLh);
    if (v > 0) return /px\s*$/i.test(cssLh) ? v : v * fontSize;
  }
  return metric;
};

// Greedy word-wrap; long words are broken char-by-char (matches word-break: break-word).
const wrapText = (ctx, text, maxWidth, letterSpacing) => {
  const breakWord = (word, out) => {
    let chunk = '';
    for (const ch of word) {
      if (chunk && measureLine(ctx, chunk + ch, letterSpacing) > maxWidth) {
        out.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    return chunk;
  };
  const out = [];
  const paragraphs = String(text).split('\n');
  for (const para of paragraphs) {
    if (para === '') {
      out.push('');
      continue;
    }
    const words = para.split(' ').filter(w => w.length);
    let cur = '';
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w;
      if (measureLine(ctx, test, letterSpacing) <= maxWidth || !cur) {
        if (!cur && maxWidth > 0 && measureLine(ctx, w, letterSpacing) > maxWidth) {
          cur = breakWord(w, out);
        } else {
          cur = test;
        }
      } else {
        out.push(cur);
        cur = maxWidth > 0 && measureLine(ctx, w, letterSpacing) > maxWidth
          ? breakWord(w, out)
          : w;
      }
    }
    out.push(cur);
  }
  return out.length ? out : [''];
};

/**
 * Largest font-size whose wrapped block fits `targetHeight`, found by monotonic bisection (a
 * bigger font never makes the block shorter, so the search converges even though the line count
 * changes in steps).
 *
 * Measured off-DOM on a Canvas in LOGICAL viewBox units, so the answer is deterministic and
 * identical in the admin preview and on the storefront.
 *
 * Two callers:
 *  - buildTextScene's overflow guard — shrink-only, `targetHeight = contentH`
 *  - the one-off font-size migration harness — `targetHeight = 100 × responsive_ratio`, which
 *    reproduces the size the old auto-fit used to land on
 */
export const fitText = (ctx, text, design, availW, targetHeight, letterSpacing, maxSize, cssLh) => {
  let lo = 1;
  let hi = maxSize;
  let best = 1;
  let bestLines = null;
  for (let i = 0; i < 12; i++) {
    const size = (lo + hi) / 2;
    ctx.font = fontStringOf(design, size);
    const lines = wrapText(ctx, text, availW, letterSpacing);
    const h = lines.length * effectiveLineHeight(ctx, design, size, cssLh);
    if (h <= targetHeight) {
      best = size;
      bestLines = lines;
      lo = size;
    } else {
      hi = size;
    }
  }
  // Nothing fit at any size. Render at the AUTHORED size rather than collapsing to the floor: the
  // target is unreachable by shrinking, so 1-unit-tall text would overflow just the same while also
  // being illegible. Happens with an absolute `line-height: Npx` over hard line breaks (the block
  // height is then independent of the font size) and with absurd padding.
  if (!bestLines) {
    best = maxSize;
    ctx.font = fontStringOf(design, best);
    bestLines = wrapText(ctx, text, availW, letterSpacing);
    return { fontSize: best, lines: bestLines };
  }
  ctx.font = fontStringOf(design, best);
  return { fontSize: best, lines: bestLines };
};

// Parse a CSS declaration string ("color: red; font-size: 20px") into { prop: value }.
// Tolerant by design: the admin re-parses this on every keystroke while the merchant is still
// typing, so malformed input must yield a partial map rather than throw.
const parseCssDecls = css => {
  const out = {};
  if (!css || typeof css !== 'string') return out;
  for (const decl of css.split(';')) {
    const i = decl.indexOf(':');
    if (i < 1) continue;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const value = decl.slice(i + 1).trim();
    if (prop && value) out[prop] = value;
  }
  return out;
};

// Declarations buildTextScene turns into SVG attributes / layout maths itself. They must be
// stripped from the leftover cssText, otherwise the inline style would beat the attribute —
// which is exactly how a `font-size` in text_styles used to desync the tspan y positions.
const CONSUMED_CSS_PROPS = [
  'color', 'fill', 'text-align', 'font-size', 'line-height', 'letter-spacing',
  'font-family', 'font-weight', 'font-style', 'text-decoration', 'text-transform',
];

const cssWithout = (css, props) => {
  if (!css) return '';
  return css
    .split(';')
    .filter(decl => {
      const i = decl.indexOf(':');
      if (i < 1) return false;
      return !props.includes(decl.slice(0, i).trim().toLowerCase());
    })
    .join(';');
};

// Padding declarations must NOT reach the <svg> element. On a <div> the background (and the
// clip-path shape) paints across the padding, so padding only pushed the text inwards. On an
// <svg> the padding is *outside* the SVG viewport, so the whole drawing — shape included —
// shrinks, and getBoundingClientRect (border box) no longer matches the viewport (content box),
// which scales the content on top of that. Callers strip these with cssWithout() and the module
// re-applies them as a text inset instead. See svg_label_and_badge.md §4.7.
// The logical longhands must be listed too: if they are not stripped they land on the <svg> and
// shrink the whole viewport, which is worse than having no effect.
const PADDING_PROPS = [
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'padding-inline', 'padding-block',
  'padding-inline-start', 'padding-inline-end', 'padding-block-start', 'padding-block-end',
];

/**
 * Resolve the padding declarations into per-side numbers, in LOGICAL viewBox units.
 *
 * Units, and they are not what they look like:
 *  - `%` resolves against `W`, the viewBox WIDTH, for all four sides — same as CSS, which also
 *    resolves vertical padding percentages against the width.
 *  - a bare number or `px` is taken as viewBox units verbatim; the `px` suffix is accepted and then
 *    IGNORED, because nothing in this pipeline knows the element's device size. So `padding: 10px`
 *    on a 100-unit-tall box means 10% of the label's height, but only `10/aspect` % of its width.
 *  - `em`/`rem`/`ch`/`calc()` are ignored (that side stays 0) — see §4.7 for why resolving `em`
 *    would be circular now that padding scales the font size.
 *
 * `padding-inline-start/end` map to left/right unconditionally; RTL is not modelled.
 */
const parsePadding = (map, W) => {
  const toPx = v => {
    if (v == null) return null;
    const s = String(v).trim();
    if (/%$/.test(s)) return (parseFloat(s) / 100) * W || 0;
    if (/^-?[\d.]+(px)?$/.test(s)) return parseFloat(s) || 0;
    return null;
  };
  const pad = { top: 0, right: 0, bottom: 0, left: 0 };
  const set = (sides, v) => {
    const px = toPx(v);
    if (px == null) return;
    for (const s of sides) pad[s] = px;
  };
  // `padding-inline`/`padding-block` take one or two values, like the shorthand they mirror.
  const setPair = (v, first, second) => {
    const parts = String(v).trim().split(/\s+/);
    set([first], parts[0]);
    set([second], parts.length > 1 ? parts[1] : parts[0]);
  };
  if (map.padding) {
    const parts = String(map.padding).trim().split(/\s+/);
    const [a, b, c, d] = parts;
    if (parts.length === 1) set(['top', 'right', 'bottom', 'left'], a);
    else if (parts.length === 2) { set(['top', 'bottom'], a); set(['left', 'right'], b); }
    else if (parts.length === 3) { set(['top'], a); set(['left', 'right'], b); set(['bottom'], c); }
    else { set(['top'], a); set(['right'], b); set(['bottom'], c); set(['left'], d); }
  }
  if (map['padding-block']) setPair(map['padding-block'], 'top', 'bottom');
  if (map['padding-inline']) setPair(map['padding-inline'], 'left', 'right');
  set(['top'], map['padding-block-start']);
  set(['bottom'], map['padding-block-end']);
  set(['left'], map['padding-inline-start']);
  set(['right'], map['padding-inline-end']);
  set(['top'], map['padding-top']);
  set(['right'], map['padding-right']);
  set(['bottom'], map['padding-bottom']);
  set(['left'], map['padding-left']);
  return pad;
};

// Padding resolved once from the container CSS, so the image path and the text path cannot disagree
// about the inset. Callers keep these declarations OFF the <svg> (see PADDING_PROPS).
const paddingOf = (cfg, W) => parsePadding(parseCssDecls(cfg.customCss), W);

// Text properties the container's own CSS (`styles`) used to cascade into the legacy
// <span class="pl-text">. The span only carried an inline value when `design` supplied one
// (see createLabelOrBadgeLegacy), so an unset design field let the container's value inherit.
// `text-align` and `line-height` are NOT here: the `.pl-text` rule in label-badge.css declares
// both on the element itself, which blocks inheritance.
const INHERITED_FROM_CONTAINER = [
  'color', 'font-family', 'font-size', 'font-weight', 'font-style',
  'letter-spacing', 'text-decoration', 'text-transform',
];

// Resolve one text property across the three cascade layers, lowest to highest:
//   container `styles`  <  design  <  `text_styles`
// Empty strings / 'default' count as "not set" so the next layer down can win, matching the
// `design.x ? ... : ''` guards the legacy span used.
const pickCss = (textCss, designValue, containerCss, prop) => {
  if (textCss[prop]) return textCss[prop];
  if (designValue !== undefined && designValue !== null && designValue !== '' && designValue !== 'default') {
    return designValue;
  }
  return containerCss[prop] || null;
};

const applyTextTransform = (text, transform) => {
  switch (transform) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'capitalize':
      return text.replace(/(^|\s)(\S)/g, (_m, sp, ch) => sp + ch.toUpperCase());
    default:
      return text;
  }
};

/* --------------------------------------------------------------------------------------------
 * Inline HTML ("rich text") in the `text` field
 *
 * The legacy renderer did `span.innerHTML += text`, so merchants could style parts of a label with
 * markup. The admin still has no rich-text editor — the Badge content field is a plain TextField
 * that REJECTS a literal newline, and its tooltip tells merchants to type `</br>` — so hand-written
 * HTML is the only line-break affordance the product offers.
 *
 * This parser turns that markup into styled RUNS. It is a pure string tokenizer on purpose:
 *  - no DOMParser/innerHTML, so the module still runs off-DOM (and under the Node stubs the two
 *    gate scripts use), and
 *  - only whitelisted tags/properties survive, and text only ever reaches the DOM through
 *    textContent — so unlike the legacy innerHTML path this cannot execute anything.
 *
 * See .claude/features/svg_label_and_badge.md §4.9.
 * -------------------------------------------------------------------------------------------- */

// Decoded in ONE left-to-right pass, so `&amp;lt;` yields the literal `&lt;` rather than `<`.
// Deliberately a small set: embedding the full HTML entity table would dwarf this module.
// `nbsp` maps to U+00A0 on purpose: the wrap routines split on U+0020, so a non-breaking space
// really does stop breaking there — the semantics come for free.
const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0' };

const decodeEntities = str => {
  if (str.indexOf('&') < 0) return str;
  return str.replace(/&(#[Xx]?[0-9A-Fa-f]+|[A-Za-z]+);/g, (m, body) => {
    if (body.charAt(0) === '#') {
      const hex = body.charAt(1) === 'x' || body.charAt(1) === 'X';
      const code = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!isFinite(code) || code < 1 || code > 0x10ffff) return m;
      try {
        return String.fromCodePoint(code);
      } catch (e) {
        return m;
      }
    }
    const hit = NAMED_ENTITIES[body.toLowerCase()];
    return hit === undefined ? m : hit;
  });
};

// Tags that never take content, so they must not go on the nesting stack — an unclosed <img> there
// would swallow the rest of the string into its style.
const RICH_VOID_TAGS = ['br', 'img', 'hr', 'wbr', 'input', 'meta', 'link', 'source', 'col'];
// Content is dropped along with the tag (they are containers, not just markup).
const RICH_DROP_TAGS = ['script', 'style', 'iframe', 'svg', 'noscript'];
// Block containers: a line break before and after, collapsed so <p>a</p><div>b</div> is two lines.
const RICH_BLOCK_TAGS = ['p', 'div', 'li', 'tr'];
// Presentational tags, mapped to the CSS they imply. Anything not listed (span, font, a, mark,
// label, abbr, and unknown tags alike) is an inline container: the tag is dropped, its `style`
// attribute still applies, its content is kept — which matches what the old strip regex did.
const RICH_TAG_CSS = {
  b: { 'font-weight': 'bold' },
  strong: { 'font-weight': 'bold' },
  i: { 'font-style': 'italic' },
  em: { 'font-style': 'italic' },
  u: { 'text-decoration': 'underline' },
  ins: { 'text-decoration': 'underline' },
  s: { 'text-decoration': 'line-through' },
  strike: { 'text-decoration': 'line-through' },
  del: { 'text-decoration': 'line-through' },
  small: { 'font-size': '0.8em' },
  big: { 'font-size': '1.2em' },
};

const RICH_COLOR_RE = /^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([^()]*\)|hsla?\([^()]*\))$/i;

/**
 * Per-run CSS whitelist. A property missing from here (or whose value fails its test) is DROPPED.
 *
 * Validating is not cosmetic: `font-weight`/`font-style`/`font-family` go straight into the Canvas
 * `ctx.font` shorthand, and an invalid shorthand makes the setter ignore the WHOLE string — leaving
 * the previous font in place. Measurement would then silently disagree with what the browser
 * paints, and the wrap points would be wrong with nothing to point at. `bolder`/`lighter` are
 * rejected for exactly that reason (illegal in the `font` shorthand), as is a font-family list.
 */
const RICH_CSS_VALIDATE = {
  color: v => (RICH_COLOR_RE.test(v) ? v : null),
  fill: v => (RICH_COLOR_RE.test(v) ? v : null),
  'font-weight': v => (/^(normal|bold|[1-9]00|1000)$/i.test(v) ? v : null),
  'font-style': v => (/^(normal|italic|oblique)$/i.test(v) ? v : null),
  'font-family': v => {
    const first = String(v).split(',')[0].trim().replace(/^["']|["']$/g, '').trim();
    return first && !/[(){};]/.test(first) ? first : null;
  },
  'font-size': v => (/^\d*\.?\d+(%|em|rem|px)?$/i.test(v) && parseFloat(v) > 0 ? v : null),
  'text-decoration': v => (/^(none|underline|overline|line-through)(\s|$)/i.test(v) ? v : null),
  'text-transform': v => (/^(uppercase|lowercase|capitalize|none)$/i.test(v) ? v : null),
};

/**
 * Validate the font declarations of a whole CSS map (`text_styles` or the container's `styles`) with
 * the SAME table the rich path applies per run.
 *
 * Only the props that reach `ctx.font` are touched: an invalid `font` shorthand makes the Canvas
 * setter ignore the entire assignment, so one bad declaration silently measures the label with
 * whatever font a previous label left on the shared canvas. A rejected value is DELETED so `pickCss`
 * falls through to the `design` layer — the app's own control — rather than to garbage.
 *
 * `font-size` is deliberately not here: the plain path resolves its units separately (see
 * buildTextScene) because it has no per-run `sizeFactor` to express `em` with.
 */
const FONT_CSS_PROPS = ['font-family', 'font-weight', 'font-style', 'text-decoration'];
const sanitizeFontCss = map => {
  let out = map;
  for (const prop of FONT_CSS_PROPS) {
    const raw = map[prop];
    if (raw == null || raw === '') continue;
    const ok = RICH_CSS_VALIDATE[prop](String(raw).trim());
    if (ok === raw) continue;
    if (out === map) out = { ...map };
    if (ok == null) delete out[prop];
    else out[prop] = ok;
  }
  return out;
};

// Not interpreted, just forwarded to the <tspan> style — same forward-compatibility deal the
// leftover `text_styles` declarations get on <text>.
const RICH_PASSTHROUGH_CSS = [
  'fill-opacity', 'opacity', 'stroke', 'stroke-width', 'paint-order', 'text-shadow',
  '-webkit-text-stroke',
];

// Guards against pathological input. Past the depth cap a tag stops contributing style (the frame
// is still pushed, so close tags keep balancing); past the run cap text merges into the last run.
const RICH_MAX_DEPTH = 8;
const RICH_MAX_RUNS = 200;

const EMPTY_RUN_CSS = {};

const sanitizeRunCss = map => {
  let out = null;
  for (const prop in map) {
    const check = RICH_CSS_VALIDATE[prop];
    if (check) {
      const ok = check(String(map[prop]).trim());
      if (ok != null) (out || (out = {}))[prop] = ok;
    } else if (RICH_PASSTHROUGH_CSS.indexOf(prop) >= 0) {
      (out || (out = {}))[prop] = map[prop];
    }
  }
  return out;
};

const sameRunCss = (a, b) => {
  if (a === b) return true;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
};

// Quote-aware: an attribute value may legally contain `>` (`style='content:">"'`), and merchants
// really do write `style='...'`. A naive /<[^>]*>/ would cut the tag in half there.
const RICH_TAG_RE =
  /<!--[\s\S]*?-->|<[!?][^>]*>|<\/?([A-Za-z][A-Za-z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;
const RICH_STYLE_ATTR_RE = /\bstyle\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i;

/**
 * Parse inline HTML into styled runs.
 *
 * Returns `{ rich, plain, runs }`. `rich` is false when the whole string collapses to a single
 * unstyled run — the common case by far — and callers then take the untouched plain-text path, so
 * ordinary labels render byte-identically to before this parser existed.
 *
 * Hard line breaks stay `'\n'` inside `run.text` (exactly what the old
 * `text.replace(/<br>/, '\n')` produced), so every wrap routine keeps its existing break handling.
 *
 * Forgiving by design — this is merchant-typed markup, not a document: an orphan close tag is
 * ignored, an unclosed tag is closed at the end, a bare `<` with no `>` stays literal, and
 * mis-nesting (`<b><i></b>`) unwinds to the matching frame.
 */
const parseRichText = src => {
  const raw = String(src == null ? '' : src);
  const runs = [];
  let rich = false;
  const stack = [];
  let dropDepth = 0;
  // A block boundary is owed before the next text — deferred so leading/trailing/doubled block
  // tags do not each produce a blank line.
  let pendingBlock = false;

  const topCss = () => (stack.length ? stack[stack.length - 1].css : EMPTY_RUN_CSS);

  const emit = text => {
    if (!text) return;
    const css = topCss();
    if (css !== EMPTY_RUN_CSS) rich = true;
    const last = runs[runs.length - 1];
    if (last && (runs.length >= RICH_MAX_RUNS || sameRunCss(last.css, css))) {
      last.text += text;
      return;
    }
    runs.push({ text, css });
  };

  const emitText = text => {
    if (!text) return;
    if (pendingBlock) {
      pendingBlock = false;
      const last = runs[runs.length - 1];
      if (last && !/\n$/.test(last.text)) emit('\n');
    }
    emit(text);
  };

  let cursor = 0;
  let m;
  RICH_TAG_RE.lastIndex = 0;
  while ((m = RICH_TAG_RE.exec(raw)) !== null) {
    if (m.index > cursor && !dropDepth) emitText(decodeEntities(raw.slice(cursor, m.index)));
    cursor = m.index + m[0].length;

    const name = m[1];
    // Comment / doctype / processing instruction: dropped outright.
    if (!name) continue;
    const tag = name.toLowerCase();
    const closing = m[0].charAt(1) === '/';

    if (RICH_DROP_TAGS.indexOf(tag) >= 0) {
      if (closing) dropDepth = Math.max(0, dropDepth - 1);
      else dropDepth++;
      continue;
    }
    if (dropDepth) continue;

    if (tag === 'br') {
      // A break is explicit: it always lands, even doubled or trailing, and it satisfies any
      // block boundary still owed.
      pendingBlock = false;
      emit('\n');
      continue;
    }
    if (RICH_VOID_TAGS.indexOf(tag) >= 0) continue;

    if (closing) {
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].tag === tag) {
          stack.length = i;
          break;
        }
      }
      if (RICH_BLOCK_TAGS.indexOf(tag) >= 0) pendingBlock = true;
      continue;
    }

    // Opening tag.
    if (RICH_BLOCK_TAGS.indexOf(tag) >= 0) pendingBlock = true;
    const attrs = m[2] || '';
    const selfClosing = /\/\s*$/.test(attrs);
    let css = topCss();
    if (stack.length < RICH_MAX_DEPTH) {
      const styleMatch = RICH_STYLE_ATTR_RE.exec(attrs);
      const declared = sanitizeRunCss({
        ...(RICH_TAG_CSS[tag] || null),
        ...(styleMatch ? parseCssDecls(decodeEntities(styleMatch[1] ?? styleMatch[2] ?? styleMatch[3] ?? '')) : null),
      });
      if (declared) css = { ...(css === EMPTY_RUN_CSS ? null : css), ...declared };
    }
    if (!selfClosing) stack.push({ tag, css });
  }
  if (cursor < raw.length && !dropDepth) emitText(decodeEntities(raw.slice(cursor)));

  const plain = runs.map(r => r.text).join('');
  return { rich: rich && runs.length > 0, plain, runs: runs.length ? runs : [{ text: '', css: EMPTY_RUN_CSS }] };
};

/**
 * Runs + the three existing cascade layers -> everything the layout needs, size-independent.
 *
 * The cascade gains a fourth, highest layer here: `styles` < `design` < `text_styles` < run CSS.
 *
 * A run's `font-size` becomes a FACTOR of the base size rather than an absolute value, which is
 * what keeps the shrink-only guard one-dimensional: bisecting a single scalar still scales every
 * run, and their relative sizes survive.
 */
const resolveRuns = (runs, effDesign, custom, inherited, baseSize) => {
  const baseTransform = custom['text-transform'] || inherited['text-transform'];
  return runs.map(run => {
    const css = run.css;
    let sizeFactor = 1;
    if (css['font-size']) {
      const s = String(css['font-size']).trim();
      const v = parseFloat(s);
      if (v > 0) {
        if (/%$/.test(s)) sizeFactor = v / 100;
        else if (/(em|rem)$/i.test(s)) sizeFactor = v;
        else if (baseSize > 0) sizeFactor = v / baseSize;
      }
    }
    let passthrough = '';
    for (const p of RICH_PASSTHROUGH_CSS) if (css[p]) passthrough += `${p}:${css[p]};`;
    return {
      text: applyTextTransform(run.text, css['text-transform'] || baseTransform),
      design: {
        ...effDesign,
        font_family: css['font-family'] || effDesign.font_family,
        font_style: {
          weight: css['font-weight'] || effDesign.font_style?.weight,
          style: css['font-style'] || effDesign.font_style?.style,
          decoration: css['text-decoration'] || effDesign.font_style?.decoration,
        },
      },
      sizeFactor: sizeFactor > 0 ? sizeFactor : 1,
      fill: css.fill || css.color || null,
      css: passthrough || null,
    };
  });
};

/**
 * Wrap styled runs into lines of segments.
 *
 * Widths use an ADDITIVE convention so a line's width is exactly what `measureLine` would report
 * for the concatenated string, however many segments it is split into:
 *
 *   segment width  = measureLine(text)                       -> letterSpacing × (len - 1)
 *   join with space = width(' ') + 2 × letterSpacing          -> the two slots the space adds
 *   join with none  =              1 × letterSpacing          -> the one slot the seam adds
 *
 * That identity is what makes a single-run line wrap at exactly the same points as the plain path.
 *
 * Whitespace is normalised away (as the plain path already does) rather than kept in the tspans:
 * no segment ever carries leading or trailing whitespace, so SVG's own whitespace collapsing
 * cannot shift anything and `xml:space` is not needed. The gap is pure geometry — each segment
 * gets an absolute `x`.
 *
 * A word split across runs (`dis<b>count</b>`) is NOT a wrap opportunity: the pieces are placed on
 * the same line unconditionally, which reproduces "never break inside a word" without having to
 * model multi-run atoms. Over-long single-run words still break character-by-character.
 */
const wrapRichText = (ctx, sruns, size, maxWidth, letterSpacing, cssLh) => {
  let fontRun = -1;
  const useFont = i => {
    if (fontRun !== i) {
      ctx.font = fontStringOf(sruns[i].design, size * sruns[i].sizeFactor);
      fontRun = i;
    }
  };

  const lines = [];
  let segs = [];
  let lineW = 0;
  let lastRun = -1;

  const finishLine = () => {
    // The line box follows its TALLEST run, so an enlarged inline run pushes the lines apart
    // instead of overlapping its neighbours. With every factor at 1 this collapses to the plain
    // path's single `lh` and `ascent`.
    let metricRun = 0;
    let maxFactor = 0;
    for (const s of segs) {
      const f = sruns[s.run].sizeFactor;
      if (f > maxFactor) {
        maxFactor = f;
        metricRun = s.run;
      }
    }
    const lineSize = size * (maxFactor > 0 ? maxFactor : 1);
    ctx.font = fontStringOf(sruns[metricRun].design, lineSize);
    fontRun = -1;
    const m = ctx.measureText('Mg');
    const ascent = m.fontBoundingBoxAscent || lineSize * 0.9;
    const descent = m.fontBoundingBoxDescent || lineSize * 0.25;
    let lh = ascent + descent || lineSize * 1.2;
    if (cssLh && cssLh !== 'normal') {
      const v = parseFloat(cssLh);
      if (v > 0) lh = /px\s*$/i.test(cssLh) ? v : v * lineSize;
    }
    lines.push({ segs, width: lineW, size: lineSize, ascent, descent, lh });
    segs = [];
    lineW = 0;
    lastRun = -1;
  };

  const gapFor = sep => {
    if (lastRun < 0) return 0;
    if (!sep) return letterSpacing;
    useFont(lastRun);
    return ctx.measureText(' ').width + 2 * letterSpacing;
  };

  // Words of the same run merge into one segment (so `<span>a b c</span>` is a single tspan, not
  // three); a different run — or a glued seam — starts a new one.
  const place = (runIdx, word, wordW, gap, sep) => {
    const tail = segs[segs.length - 1];
    if (tail && tail.run === runIdx) {
      tail.text += sep ? ' ' + word : word;
      tail.w += gap + wordW;
    } else {
      segs.push({ run: runIdx, text: word, w: wordW, gap });
    }
    lineW += gap + wordW;
    lastRun = runIdx;
  };

  const breakWord = (runIdx, word) => {
    let chunk = '';
    for (const ch of word) {
      useFont(runIdx);
      if (chunk && measureLine(ctx, chunk + ch, letterSpacing) > maxWidth) {
        segs.push({ run: runIdx, text: chunk, w: measureLine(ctx, chunk, letterSpacing), gap: 0 });
        lineW = segs[segs.length - 1].w;
        lastRun = runIdx;
        finishLine();
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    return chunk;
  };

  // Break an over-long word, then place whatever is left as the start of the fresh line.
  const placeBroken = (runIdx, word) => {
    const rest = breakWord(runIdx, word);
    if (!rest) return;
    useFont(runIdx);
    place(runIdx, rest, measureLine(ctx, rest, letterSpacing), 0, false);
  };

  const addWord = (runIdx, word, sep) => {
    const gap = gapFor(sep);
    useFont(runIdx);
    const wordW = measureLine(ctx, word, letterSpacing);
    // Glued to the previous piece (no whitespace between them): not a break opportunity, so it
    // goes on this line whatever the width. The shrink-only guard is what stops overflow.
    if (!sep && lastRun >= 0) {
      place(runIdx, word, wordW, gap, false);
      return;
    }
    const tooWide = maxWidth > 0 && wordW > maxWidth;
    if (lineW + gap + wordW <= maxWidth || lastRun < 0) {
      if (lastRun < 0 && tooWide) placeBroken(runIdx, word);
      else place(runIdx, word, wordW, gap, sep);
      return;
    }
    finishLine();
    if (tooWide) {
      placeBroken(runIdx, word);
    } else {
      useFont(runIdx);
      place(runIdx, word, wordW, 0, false);
    }
  };

  // Token walk: maximal word / whitespace / hard-break tokens, run by run. `pendingSep` carries a
  // space across a run boundary, and its absence is what marks a glued seam.
  let pendingSep = false;
  for (let i = 0; i < sruns.length; i++) {
    const text = sruns[i].text;
    let j = 0;
    while (j < text.length) {
      const ch = text.charAt(j);
      if (ch === '\n') {
        finishLine();
        pendingSep = false;
        j++;
        continue;
      }
      if (ch === ' ' || ch === '\t' || ch === '\r') {
        pendingSep = true;
        j++;
        continue;
      }
      let k = j;
      while (k < text.length && ' \t\r\n'.indexOf(text.charAt(k)) < 0) k++;
      addWord(i, text.slice(j, k), pendingSep);
      pendingSep = false;
      j = k;
    }
  }
  finishLine();
  return lines;
};

// Largest size whose wrapped rich block fits `targetHeight`. Same 12-step bisection as `fitText`;
// height is the sum of the per-line line-heights because they can differ.
const fitRichText = (ctx, sruns, availW, targetHeight, letterSpacing, cssLh, maxSize) => {
  const blockH = lines => lines.reduce((a, l) => a + l.lh, 0);
  let lo = 1;
  let hi = maxSize;
  let best = 1;
  let bestLines = null;
  for (let i = 0; i < 12; i++) {
    const size = (lo + hi) / 2;
    const lines = wrapRichText(ctx, sruns, size, availW, letterSpacing, cssLh);
    if (blockH(lines) <= targetHeight) {
      best = size;
      bestLines = lines;
      lo = size;
    } else {
      hi = size;
    }
  }
  if (!bestLines) bestLines = wrapRichText(ctx, sruns, best, availW, letterSpacing, cssLh);
  return { fontSize: best, lines: bestLines };
};

// Build <text> for the rich path. Returns the same kind of node the plain path builds, so the
// caller's clip/group/style handling is shared.
const buildRichText = (parsed, ctx, opts) => {
  const { design, effDesign, custom, inherited, baseSize, letterSpacing, availW, contentH,
    centerX, centerY, layout, W, H } = opts;

  const sruns = resolveRuns(parsed.runs, effDesign, custom, inherited, baseSize);
  const cssLh = custom['line-height'];

  let fontSize = baseSize;
  let lines = wrapRichText(ctx, sruns, baseSize, availW, letterSpacing, cssLh);
  // Shrink-only guard, identical in spirit to the plain path: never enlarges, so the authored
  // font_size stands whenever the block fits. Scaling one scalar keeps run ratios intact.
  if (lines.reduce((a, l) => a + l.lh, 0) > contentH) {
    const fitted = fitRichText(ctx, sruns, availW, contentH, letterSpacing, cssLh, baseSize);
    fontSize = fitted.fontSize;
    lines = fitted.lines;
  }

  // text-align moves the line's start x; `text-anchor` cannot centre a multi-segment line, so the
  // geometry does the aligning and the anchor is always `start`.
  const align = (custom['text-align'] || '').toLowerCase();
  const alignStart = align === 'left' || align === 'start';
  const alignEnd = align === 'right' || align === 'end';
  const anchorX = alignStart ? centerX - availW / 2 : alignEnd ? centerX + availW / 2 : centerX;

  const baseFill = custom.fill || pickCss(custom, design.text_color, inherited, 'color') || '#000000';
  const baseFamily = effDesign.font_family && effDesign.font_family !== 'default'
    ? `'${effDesign.font_family}'`
    : null;
  const baseWeight = effDesign.font_style?.weight || null;
  const baseStyle = effDesign.font_style?.style || null;
  const baseDecoration = effDesign.font_style?.decoration || null;

  const textAttrs = {
    class: 'pl-text-svg',
    'text-anchor': 'start',
    x: anchorX,
    fill: baseFill,
    'font-size': fontSize,
    'font-family': baseFamily,
    'font-weight': baseWeight,
    'font-style': baseStyle,
    'text-decoration': baseDecoration,
    'letter-spacing': letterSpacing || null,
  };
  if (layout.rotate === 'auto') {
    const deg = Math.atan(H / W) * (180 / Math.PI);
    textAttrs.transform = `rotate(${deg} ${centerX} ${centerY})`;
  } else if (typeof layout.rotate === 'number') {
    textAttrs.transform = `rotate(${layout.rotate} ${centerX} ${centerY})`;
  }

  const totalH = lines.reduce((a, l) => a + l.lh, 0);
  let y = centerY - totalH / 2;
  const tspans = [];
  for (const line of lines) {
    const baseline = y + line.ascent;
    y += line.lh;
    const startX = alignStart
      ? centerX - availW / 2
      : alignEnd
        ? centerX + availW / 2 - line.width
        : centerX - line.width / 2;
    if (!line.segs.length) {
      tspans.push(sceneNode('tspan', { x: startX, y: baseline }, { text: '' }));
      continue;
    }
    let x = startX;
    for (const s of line.segs) {
      x += s.gap;
      const run = sruns[s.run];
      const runSize = fontSize * run.sizeFactor;
      const family = run.design.font_family && run.design.font_family !== 'default'
        ? `'${run.design.font_family}'`
        : null;
      // Only spell out what DIFFERS from the value hoisted on <text>; equal values are noise and
      // `null` is skipped by both emitters.
      const attrs = {
        x,
        y: baseline,
        fill: run.fill && run.fill !== baseFill ? run.fill : null,
        'font-size': runSize !== fontSize ? runSize : null,
        'font-family': family !== baseFamily ? family : null,
        'font-weight': (run.design.font_style?.weight || null) !== baseWeight
          ? run.design.font_style?.weight || null
          : null,
        'font-style': (run.design.font_style?.style || null) !== baseStyle
          ? run.design.font_style?.style || null
          : null,
        'text-decoration': (run.design.font_style?.decoration || null) !== baseDecoration
          ? run.design.font_style?.decoration || null
          : null,
      };
      tspans.push(sceneNode('tspan', attrs, run.css ? { text: s.text, style: run.css } : { text: s.text }));
      x += s.w;
    }
  }

  return sceneNode('text', textAttrs, { children: tspans });
};

// Wrap a <text> in a <g> so the shape clip lives in the (unrotated) shape space while the text
// keeps its own rotation inside it — matching the old clip-path-clips-the-shape / span-rotates
// behaviour. Shared by the plain and rich paths so they cannot clip differently.
const clipAndGroup = (cfg, shapeNode, textEl) => {
  let clip = null;
  let clipRef = null;
  if (!cfg.hasImage && shapeNode) {
    const clipId = `deco-clip-${cfg.uid}`;
    clip = sceneNode('clipPath', { id: clipId }, { children: [sceneClone(shapeNode)] });
    clipRef = `url(#${clipId})`;
  }
  return { clip, group: sceneNode('g', { 'clip-path': clipRef }, { children: [textEl] }) };
};

// Build <text>/<tspan> inside the svg (and a shape clip so overflow matches clip-path).
// Returns { clip, group }: `clip` is the <clipPath> node the caller must put in <defs> (null when
// the text is not clipped), `group` is the <g> wrapping the <text>. Data only — see "Scene nodes".
const buildTextScene = (cfg, W, H, shapeNode) => {
  const design = cfg.design;
  const ctx = measureCtx();
  const layout = SHAPE_TEXT_LAYOUT[cfg.shapeClass] || {};
  const borderSize = cfg.border ? parseFloat(cfg.border.size) || 0 : 0;

  // The shape always fills the whole box. The text sits inside a content box inset by the
  // border plus any `padding` from the container CSS — which the caller kept off the <svg>
  // precisely so the shape would not shrink with it.
  const cssPad = paddingOf(cfg, W);
  const insetT = borderSize + cssPad.top;
  const insetR = borderSize + cssPad.right;
  const insetB = borderSize + cssPad.bottom;
  const insetL = borderSize + cssPad.left;

  const widthFactor = layout.widthFactor || 1;
  const padL = layout.padL != null ? layout.padL : (layout.padInline || 0);
  const padR = layout.padR != null ? layout.padR : (layout.padInline || 0);
  // The text band spans [padL, 1 - padR] of the region, so its centre sits (padL - padR)/2 away
  // from the centre of the box. This used to be hardcoded to 0.5, which meant asymmetric padding
  // only narrowed `availW` — symmetrically, around the box centre — and never moved the text. The
  // pointed/chevron/arrow shapes therefore drew their text off-centre relative to the shape.
  // The value matches what the legacy renderer paints: `.pl-text` is `width: 100%` +
  // `text-align: center` + `padding-left/right` in %, and under `box-sizing: border-box` (which
  // every modern Shopify theme sets globally) that lands the glyph centre at exactly
  // 0.5 + (padL - padR)/2 — measured in a browser, not derived on paper.
  // Shapes that declare `ax` explicitly (trapezoid/triangle) still win.
  const ax = layout.ax != null ? layout.ax : 0.5 + (padL - padR) / 2;
  const ay = layout.ay != null ? layout.ay : 0.5;

  const contentW = Math.max(1, W - insetL - insetR);
  const contentH = Math.max(1, H - insetT - insetB);
  const regionW = contentW * widthFactor;
  const availW = Math.max(1, regionW * (1 - padL - padR));
  // ax/ay are fractions of the *content* box.
  const centerX = insetL + contentW * ax;
  const centerY = insetT + contentH * ay;

  // --- merchant CSS (text_styles) -------------------------------------------------------
  // On the legacy <span> this string was simply appended to style.cssText. On SVG <text> that
  // is not enough: some properties need a different name (color -> fill, text-align ->
  // text-anchor) and — more importantly — anything that changes text metrics must be folded
  // into the Canvas measurement, or wrapText/fitText would measure a different font than the
  // browser paints. See .claude/features/svg_label_and_badge.md §4.7.
  const custom = parseCssDecls(cfg.customTextCss);

  // The container's own CSS (`styles`) is the lowest cascade layer: on the legacy DOM it sat on
  // the wrapping <div> and inherited into the <span> for any property `design` left unset.
  // Only the inheritable text properties are taken; the rest of `styles` stays on the <svg>
  // root (applied by the caller) exactly as before.
  const containerAll = parseCssDecls(cfg.customCss);
  const inherited = {};
  for (const p of INHERITED_FROM_CONTAINER) {
    if (containerAll[p]) inherited[p] = containerAll[p];
  }

  // Effective design: container < design < text_styles. Used for BOTH the Canvas measurement
  // and the emitted attributes so the two can never disagree.
  //
  // The CSS layers are sanitised with the SAME table the rich path uses, because they end up in
  // `ctx.font` and an invalid `font` shorthand makes the Canvas setter ignore the WHOLE assignment —
  // leaving the shared measurement canvas on whatever font a PREVIOUS label set. Unsanitised, this
  // path turned `font-weight: bolder` into a measurement against an unrelated font while the browser
  // painted it correctly, and `font-family: Arial, sans-serif` into `font-family="'Arial, sans-serif'"`
  // (one family name that matches nothing, with the declaration also stripped from the leftover style
  // by CONSUMED_CSS_PROPS — so the font was lost entirely). The same declarations already worked on a
  // label containing markup, which is the asymmetry this removes.
  const safeCustom = sanitizeFontCss(custom);
  const safeInherited = sanitizeFontCss(inherited);
  const effDesign = {
    ...design,
    font_family: pickCss(safeCustom, design.font_family, safeInherited, 'font-family'),
    font_style: {
      weight: pickCss(safeCustom, design.font_style?.weight, safeInherited, 'font-weight'),
      style: pickCss(safeCustom, design.font_style?.style, safeInherited, 'font-style'),
      decoration: pickCss(safeCustom, design.font_style?.decoration, safeInherited, 'text-decoration'),
    },
  };

  // cfg.isMobile is passed explicitly by BOTH callers now (the admin has its own device toggle; the
  // storefront derives it from the same 768 comparison it uses for the box). The 992 fallback is only
  // for a caller that forgets — it must never be the value a real render uses, or the label gets a
  // desktop box with a mobile font size between 769 and 991px.
  const isMobile = typeof cfg.isMobile === 'boolean' ? cfg.isMobile : window.innerWidth < 992;
  const key = cfg.page === 'product'
    ? `font_size_product${isMobile ? '_mobile' : ''}`
    : `font_size${isMobile ? '_mobile' : ''}`;
  // The `font_size_product` step is PAGE-SCOPED. It belongs on a product page — `font_size_product_mobile`
  // legitimately falls back to `font_size_product` before `font_size` — but on a collection page it
  // leaked the product-page size onto the card. It used to be unconditional, so a record with
  // `font_size_mobile` unset and `font_size_product` set rendered collection cards at the product size.
  // The three other implementations of this chain (Size.jsx, BackgroundPreview.jsx, and the legacy
  // renderer) are all page-scoped exactly like this.
  let baseSize = parseFloat(
    cfg.page === 'product'
      ? design[key] || design.font_size_product || design.font_size || inherited['font-size'] || 14
      : design[key] || design.font_size || inherited['font-size'] || 14
  );
  // `text_styles` font-size always wins here. (There used to be a `!cfg.autoResponsive` guard meant
  // to reproduce a legacy contract, but no caller has ever set `cfg.autoResponsive` — the SVG branch
  // drops auto_responsive by design — so the guard could never fire. Removed rather than left as
  // dead code that reads like an implemented rule.)
  if (custom['font-size']) {
    const raw = String(custom['font-size']).trim();
    const v = parseFloat(raw);
    if (v > 0) {
      // Bare/px are viewBox units; % and em/rem are relative to the size resolved above. Any other
      // unit is REJECTED rather than parseFloat'd — `1.2em` used to become baseSize 1.2, i.e. 1.2% of
      // the label height, which is invisible text.
      if (/^\d*\.?\d+(px)?$/i.test(raw)) baseSize = v;
      else if (/^\d*\.?\d+%$/.test(raw)) baseSize *= v / 100;
      else if (/^\d*\.?\d+r?em$/i.test(raw)) baseSize *= v;
    }
  }

  // `font_size` is a share of the label HEIGHT, so padding — which takes height away from the text —
  // has to scale it. This is the ONLY thing that makes symmetric padding visible: it leaves the
  // anchor exactly where it was (`P + (H - 2P)/2 === H/2`), so without this the effects were a
  // slightly narrower wrap width and an earlier shrink-guard trip, neither of which a short
  // single-line label ever shows. Merchants read that, correctly, as "padding does nothing".
  //
  // Scaled by the PADDING share only, NOT the whole inset: `border` also insets the text and has
  // done since the start, so folding it in here would silently shrink every bordered label.
  // Applied after the text_styles override so both authored sources scale.
  const padScale = Math.max(0.05, (H - cssPad.top - cssPad.bottom) / H);
  baseSize *= padScale;

  const lsRaw = pickCss(custom, design.letter_spacing, inherited, 'letter-spacing');
  const letterSpacing = lsRaw != null ? parseFloat(lsRaw) || 0 : 0;

  // Inline HTML (`<span style>`, `<b>`, `<br>`, …) becomes styled runs. A string that collapses to
  // a single UNSTYLED run — the overwhelming majority of labels — falls through to the plain path
  // below, which is left exactly as it was: ordinary text still renders byte-for-byte identically.
  const parsed = parseRichText(cfg.text);
  if (parsed.rich) {
    const richEl = buildRichText(parsed, ctx, {
      design, effDesign, custom, inherited, baseSize, letterSpacing,
      availW, contentH, centerX, centerY, layout, W, H,
    });
    const richLeftover = cssWithout(cfg.customTextCss, CONSUMED_CSS_PROPS);
    if (richLeftover) richEl.style = richLeftover;
    return clipAndGroup(cfg, shapeNode, richEl);
  }

  // text-transform changes glyph widths, so apply it to the string before wrapping/fitting.
  // `parsed.plain` is the tag-free, entity-decoded text — for a string with no markup at all it is
  // the input verbatim.
  const text = applyTextTransform(
    parsed.plain,
    custom['text-transform'] || inherited['text-transform']
  );

  // `font_size` is authoritative (a share of VIEWBOX_HEIGHT) — the logical viewBox already scales
  // the text with the label, so there is no auto-fit feature here. `auto_responsive` /
  // `responsive_ratio` are ignored; they still drive the legacy branch.
  //
  // Wrapping is measured in logical units, so line breaks are identical in the admin preview and
  // on the storefront regardless of how big either box happens to be.
  // Read BEFORE the guard: the guard has to measure the block the way it will be painted.
  const cssLh = custom['line-height'];

  ctx.font = fontStringOf(effDesign, baseSize);
  let lines = wrapText(ctx, text, availW, letterSpacing);
  let fontSize = baseSize;

  // Overflow guard — SHRINK ONLY. Protects against text that cannot fit at the authored size
  // (long text, a tall shape, a big font_size) spilling outside the shape. It never enlarges, so
  // `font_size` remains the size the merchant sees whenever the text does fit.
  if (lines.length * effectiveLineHeight(ctx, effDesign, baseSize, cssLh) > contentH) {
    const fitted = fitText(ctx, text, effDesign, availW, contentH, letterSpacing, baseSize, cssLh);
    fontSize = fitted.fontSize;
    lines = fitted.lines;
  }

  // Vertical centring via font metrics (baseline-relative) so it matches the old
  // line-height:normal + flex-centre span. dominant-baseline:middle is unreliable across fonts.
  ctx.font = fontStringOf(effDesign, fontSize);
  const metrics = ctx.measureText('Mg');
  const ascent = metrics.fontBoundingBoxAscent || fontSize * 0.9;
  const descent = metrics.fontBoundingBoxDescent || fontSize * 0.25;
  let lh = ascent + descent || fontSize * 1.2;
  // line-height: unitless multiplier or px. `normal` keeps the font-metric height. Same rule the
  // guard above already applied via effectiveLineHeight — `cssLh` is hoisted so they cannot diverge.
  if (cssLh && cssLh !== 'normal') {
    const v = parseFloat(cssLh);
    if (v > 0) lh = /px\s*$/i.test(cssLh) ? v : v * fontSize;
  }
  const n = lines.length;
  // First line's baseline: top of the centred n-line block + ascent.
  const firstBaseline = centerY - (n * lh) / 2 + ascent;

  // text-align -> text-anchor. The anchor x must move to the matching edge of the wrap region
  // (which is availW wide, centred on centerX), otherwise the text just shifts off-centre.
  const align = (custom['text-align'] || '').toLowerCase();
  let anchor = 'middle';
  let anchorX = centerX;
  if (align === 'left' || align === 'start') {
    anchor = 'start';
    anchorX = centerX - availW / 2;
  } else if (align === 'right' || align === 'end') {
    anchor = 'end';
    anchorX = centerX + availW / 2;
  }

  const textAttrs = {
    class: 'pl-text-svg',
    'text-anchor': anchor,
    x: anchorX,
    // SVG paints text with `fill`; a bare `color` would be inert here. Full cascade:
    // text_styles fill/color > design.text_color > container `styles` color > black.
    fill: custom.fill || pickCss(custom, design.text_color, inherited, 'color') || '#000000',
    'font-size': fontSize,
    'font-family': effDesign.font_family && effDesign.font_family !== 'default'
      ? `'${effDesign.font_family}'`
      : null,
    'font-weight': effDesign.font_style?.weight || null,
    'font-style': effDesign.font_style?.style || null,
    'text-decoration': effDesign.font_style?.decoration || null,
    'letter-spacing': letterSpacing || null,
  };

  // `transform` is added last so the attribute order matches what the DOM builder used to produce.
  if (layout.rotate === 'auto') {
    const deg = Math.atan(H / W) * (180 / Math.PI);
    textAttrs.transform = `rotate(${deg} ${centerX} ${centerY})`;
  } else if (typeof layout.rotate === 'number') {
    textAttrs.transform = `rotate(${layout.rotate} ${centerX} ${centerY})`;
  }

  const textEl = sceneNode('text', textAttrs, {
    children: lines.map((ln, i) =>
      sceneNode('tspan', { x: anchorX, y: firstBaseline + i * lh }, { text: ln })
    ),
  });

  // Only the declarations we did NOT turn into attributes/layout go into style — keeping them
  // would let the inline style override the attribute we just computed.
  const leftoverCss = cssWithout(cfg.customTextCss, CONSUMED_CSS_PROPS);
  if (leftoverCss) textEl.style = leftoverCss;

  return clipAndGroup(cfg, shapeNode, textEl);
};

/**
 * Recompute the aspect from `cfg` and write it to BOTH places that must agree: the cfg fields the
 * viewBox is built from, and the element's CSS aspect-ratio.
 *
 * Always go through this instead of setting `style.aspectRatio` by hand — a viewBox aspect that
 * disagrees with the CSS aspect is exactly what makes `preserveAspectRatio="none"` stretch the text.
 */
export const applyAspect = (svg, cfg) => {
  const { viewW, viewH, cssAspect } = effectiveAspect(cfg);
  cfg.viewW = viewW;
  cfg.viewH = viewH;
  if (svg) svg.style.aspectRatio = cssAspect;
  return { viewW, viewH, cssAspect };
};

// Describe the whole drawing for a `__decoLayout` config, as plain data (see "Scene nodes").
// No DOM, no React — the only browser API touched is the off-DOM Canvas used to measure text.
//
// The viewBox is a FIXED LOGICAL coordinate system (VIEWBOX_HEIGHT units tall, width derived from
// the label's aspect) — deliberately NOT the measured pixel box. The browser then scales the whole
// drawing to whatever size the element ends up, so the ratio between font-size and the label stays
// constant everywhere: the same label looks identical in the admin preview and on a storefront card
// three times bigger. Everything inside the viewBox (font-size, letter-spacing, stroke-width,
// border-radius, padding) is in these units and scales as one vector.
//
// Consequence: no getBoundingClientRect anywhere in the draw path, so there is nothing to go stale
// on resize either. See .claude/features/svg_label_and_badge.md §4.3.
export const buildSvgScene = cfg => {
  // viewW/viewH are written by applyAspect — read them rather than recomputing, so the viewBox can
  // never disagree with the CSS aspect-ratio the element carries.
  const W = parseFloat(cfg.viewW) || VIEWBOX_HEIGHT;
  const H = parseFloat(cfg.viewH) || VIEWBOX_HEIGHT;

  const defs = [];
  const children = [];

  const borderSize = cfg.border ? parseFloat(cfg.border.size) || 0 : 0;
  const hasBorder = borderSize > 0 && cfg.border && cfg.border.style && cfg.border.style !== 'none';

  let shapeNode = null;
  if (cfg.hasImage) {
    // Padding insets the image, the same way it insets the text. On the legacy DOM the <img> was
    // `width: 100%` of the div's CONTENT box, so padding shrank it there too; the SVG branch used to
    // ignore padding entirely for image labels (buildTextScene, the only reader, never runs without
    // text) which made the field completely inert. `xMidYMid meet` letterboxes inside the inset box.
    const imgPad = paddingOf(cfg, W);
    children.push(
      sceneNode('image', {
        href: cfg.image,
        'xlink:href': cfg.image, // fallback for old browsers
        x: imgPad.left,
        y: imgPad.top,
        width: Math.max(1, W - imgPad.left - imgPad.right),
        height: Math.max(1, H - imgPad.top - imgPad.bottom),
        preserveAspectRatio: cfg.ratioSize ? 'xMidYMid meet' : 'none',
        'aria-label': cfg.alt || null,
      })
    );
    // Full-box rect: the clip region for text drawn on top of the image. Deliberately NOT inset —
    // the text's own inset already comes from centerX/centerY inside buildTextScene.
    shapeNode = sceneNode('rect', { x: 0, y: 0, width: W, height: H });
  } else {
    let fill;
    if (cfg.textType === 1) {
      const gid = `deco-grad-${cfg.uid}`;
      defs.push(buildGradient(gid, cfg.gradientDir, cfg.startColor, cfg.endColor));
      fill = `url(#${gid})`;
    } else if (cfg.textType === 2) {
      // text_type 2 = no fill. The legacy DOM set neither backgroundColor nor background, so
      // the shape showed through transparent; shape_color must NOT be applied here.
      fill = 'transparent';
    } else {
      fill = cfg.shapeColor || 'transparent';
    }
    // An SVG stroke straddles the edge it is painted on: half inside, half outside. Drawn on a shape
    // spanning the full 0..W/0..H box that put half the border OUTSIDE the viewBox — and since the
    // root carries `overflow: visible`, outside the label's own box too. Two things merchants saw:
    // the label occupied more room than the size they set (able to overlap theme content), and the
    // border always looked half as thick as the number they typed.
    //
    // So build the shape inset by half the stroke width. `inset` is 0 without a border, which keeps
    // unbordered labels byte-identical by construction. The clip path for the text is a clone of this
    // node, so it inherits the inset and keeps clipping to the shape that is actually visible.
    const inset = hasBorder ? borderSize / 2 : 0;
    shapeNode = buildShape(
      cfg.shapeClass,
      Math.max(1, W - inset * 2),
      Math.max(1, H - inset * 2),
      parseFloat(cfg.borderRadius) || 0
    );
    shapeNode.attrs.fill = fill;
    if (hasBorder) {
      shapeNode.attrs.stroke = cfg.border.color;
      shapeNode.attrs['stroke-width'] = borderSize;
      shapeNode.attrs.transform = `translate(${inset} ${inset})`;
    }
    children.push(shapeNode);
  }

  if (cfg.text) {
    const { clip, group } = buildTextScene(cfg, W, H, shapeNode);
    if (clip) defs.push(clip);
    children.push(group);
  }

  if (cfg.link) {
    children.push(
      sceneNode(
        'a',
        { target: cfg.link.target, href: cfg.link.href, 'xlink:href': cfg.link.href },
        { children: [sceneNode('rect', { x: 0, y: 0, width: W, height: H, fill: 'transparent' })] }
      )
    );
  }

  return {
    viewBox: `0 0 ${W} ${H}`,
    // Safe because callers keep the element's CSS aspect-ratio equal to the viewBox aspect (see
    // effectiveAspect). Kept as `none` so the shape still fills the box exactly — matching the old
    // clip-path — if merchant CSS ever breaks that invariant.
    preserveAspectRatio: 'none',
    // Same numbers as applyAspect writes, exposed here so a declarative renderer can set the
    // element's aspect-ratio from the same source as the viewBox.
    cssAspect: `${W} / ${H}`,
    defs,
    children,
  };
};

const emitSceneNode = node => {
  const el = svgCreate(node.tag, node.attrs);
  if (node.style) el.style.cssText += node.style;
  if (node.text != null) el.textContent = node.text;
  if (node.children) for (const c of node.children) el.appendChild(emitSceneNode(c));
  return el;
};

// Imperative emitter: scene data -> real DOM under `svg`. The storefront path.
export const renderSceneInto = (svg, scene) => {
  svg.setAttribute('viewBox', scene.viewBox);
  svg.setAttribute('preserveAspectRatio', scene.preserveAspectRatio);

  // Keep the existing <image> when the href has not changed, instead of wiping it with everything
  // else. A freshly created <image> starts undecoded, so every redraw blanks the picture for at
  // least a frame — in the admin preview, where this runs on every edit, that reads as a flicker.
  // Same element + same href = no reload. Its geometry attributes are rewritten below.
  // NOTE: this is the ONE exception to "the emitter clears all children".
  const imageNode = scene.children.find(n => n.tag === 'image') || null;
  const prevImage = imageNode ? svg.querySelector('image') : null;
  const reuseImage =
    prevImage && prevImage.getAttribute('href') === imageNode.attrs.href ? prevImage : null;
  for (let child = svg.firstChild; child; ) {
    const next = child.nextSibling;
    if (child !== reuseImage) svg.removeChild(child);
    child = next;
  }

  const defsEl = svgCreate('defs');
  svg.appendChild(defsEl);
  for (const d of scene.defs) defsEl.appendChild(emitSceneNode(d));

  for (const n of scene.children) {
    if (n === imageNode && reuseImage) {
      for (const k in n.attrs) {
        // href/xlink:href are equal by definition here, and re-setting them can trigger a refetch.
        if (k === 'href' || k === 'xlink:href') continue;
        if (n.attrs[k] === null || n.attrs[k] === undefined) reuseImage.removeAttribute(k);
        else reuseImage.setAttribute(k, n.attrs[k]);
      }
      // Moving an element that is already a child does NOT reload it — it only reorders. Appending
      // here is what keeps the image ahead of the text that follows, so it still paints underneath.
      svg.appendChild(reuseImage);
    } else {
      svg.appendChild(emitSceneNode(n));
    }
  }
};

// (Re)build the svg contents for a node from `svg.__decoLayout`.
export const layoutSvgNode = svg => {
  const cfg = svg && svg.__decoLayout;
  if (!cfg) return;
  renderSceneInto(svg, buildSvgScene(cfg));
};

/**
 * Resolve once the web font for `family` is genuinely usable for measurement.
 *
 * Canvas `measureText` never triggers a font load, so the first draw can easily be measured with
 * the FALLBACK font while the browser paints the real one — wrong wrap points, wrong shrink, and
 * nothing to point at. Measured on a real page: two labels with identical config created a few
 * seconds apart came out `SUMMER / CLEARANCE / EVENT` at 30.0 and `SUMMER / CLEARANCE EVENT` at
 * 25.1, and the first was never corrected.
 *
 * Three obvious APIs all fail here, each verified in a browser rather than assumed:
 *  - `document.fonts.status` is GLOBAL and reads 'loaded' the instant after loadFont() appends its
 *    <link> — the face has not been discovered yet. The old `status !== 'loaded'` guard therefore
 *    skipped the redraw entirely, which is exactly the bug.
 *  - `document.fonts.check()` returns TRUE for a family with no registered face at all (it answers
 *    "can this text be rendered", falling back), so it cannot distinguish loaded from missing.
 *  - a single `document.fonts.load()` resolves with ZERO faces while the <link> CSS is still in
 *    flight, proving nothing.
 *
 * So: retry on `loadingdone` until `load()` actually reports a face. Event-driven, no polling loop;
 * one shared promise per family however many labels use it; a timeout so a font that never arrives
 * cannot leak the listener.
 */
const FONT_WAIT_TIMEOUT_MS = 5000;
const _fontWaits = new Map();
const _fontSettled = new Set();
const fontReady = family => {
  // Already usable when this label was first drawn -> the first draw was correct, redrawing would
  // be pure cost (and on a collection page that is one wasted redraw per label).
  if (_fontSettled.has(family)) return null;
  if (_fontWaits.has(family)) return _fontWaits.get(family);

  const want = `100px '${family}'`;
  const promise = new Promise(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      _fontSettled.add(family);
      if (document.fonts.removeEventListener) document.fonts.removeEventListener('loadingdone', onDone);
      clearTimeout(timer);
      resolve();
    };
    const tryLoad = () => {
      document.fonts
        .load(want)
        .then(faces => {
          if (faces && faces.length) finish();
        })
        .catch(() => {});
    };
    const onDone = () => tryLoad();
    const timer = setTimeout(finish, FONT_WAIT_TIMEOUT_MS);
    if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', onDone);
    tryLoad();
  });
  _fontWaits.set(family, promise);
  return promise;
};

// Schedule the (re)layout that follows web-font loading. No rAF pass and no clientHeight loop —
// the logical viewBox makes the drawing resolution-independent, so there is nothing to re-measure.
const scheduleSvgLayout = svg => {
  const cfg = svg.__decoLayout;
  if (!cfg) return;

  // Image labels set to keep their ratio only learn their aspect once the image has loaded. Probe
  // the natural size, then recompute BOTH the viewBox and the CSS aspect-ratio from it so the two
  // never disagree. Runs for clones too — cloneNode does not carry the async probe over.
  if (cfg.hasImage && cfg.ratioSize && !cfg.imageAspect && cfg.image) {
    const probe = new Image();
    probe.onload = () => {
      // Write to whatever config the node holds NOW, not the one captured when the probe started:
      // the admin preview replaces __decoLayout on every edit, and layoutSvgNode always reads the
      // live one. Updating the captured (detached) config instead would set the CSS aspect-ratio
      // from the image while the viewBox still came from width/height — the exact disagreement that
      // makes preserveAspectRatio="none" stretch the drawing.
      const live = svg.__decoLayout;
      if (!live || live.image !== cfg.image) return;
      if (probe.naturalWidth && probe.naturalHeight) {
        live.imageAspect = probe.naturalWidth / probe.naturalHeight;
        applyAspect(svg, live);
        layoutSvgNode(svg);
      }
    };
    probe.src = cfg.image;
  }

  // No measured pass any more — the logical viewBox makes the drawing resolution-independent, so
  // there is nothing to re-measure on rAF or on resize. The only reason to redraw is that Canvas
  // text metrics change once a web font finishes loading, which can shift where lines wrap.
  const family = cfg.design && cfg.design.font_family;
  if (family && family !== 'default' && document.fonts && typeof document.fonts.load === 'function') {
    const wait = fontReady(family);
    if (wait) wait.then(() => layoutSvgNode(svg));
  } else if (document.fonts && document.fonts.status !== 'loaded') {
    document.fonts.ready.then(() => layoutSvgNode(svg));
  }
};

// Clone a label/badge <svg> for group stacking. cloneNode does NOT copy the __decoLayout JS
// property, so we re-attach a fresh copy (with a NEW uid so gradient/clip ids stay unique) and
// schedule its measured layout — making the clone behave exactly like a freshly-created node.
export const cloneLabelBadgeNode = node => {
  const clone = node.cloneNode(true);
  if (node.__decoLayout) {
    clone.__decoLayout = { ...node.__decoLayout, uid: ++decoUid };
    // Must redraw immediately: cloneNode copied the original's children, whose <linearGradient> /
    // <clipPath> ids still carry the ORIGINAL uid. Leaving them would put duplicate ids in the
    // document and every url(#…) would resolve to whichever came first.
    layoutSvgNode(clone);
    scheduleSvgLayout(clone);
  }
  return clone;
};

export {
  SVG_NS,
  XLINK_NS,
  REM_PX,
  svgCreate,
  SHAPE_POLYGONS,
  SHAPE_ROUNDED,
  SHAPE_TEXT_LAYOUT,
  roundedRectPath,
  buildShape,
  buildGradient,
  measureCtx,
  fontStringOf,
  measureLine,
  lineHeightOf,
  wrapText,
  effectiveLineHeight,
  fontReady,
  parseRichText,
  wrapRichText,
  fitRichText,
  parseCssDecls,
  cssWithout,
  PADDING_PROPS,
  sceneNode,
  buildTextScene,
  scheduleSvgLayout,
  effectiveAspect,
  VIEWBOX_HEIGHT,
  ONE_ONE_SHAPES,
  nextDecoUid,
}
